import { policyMetadata, type PolicyAcceptancePayload } from './policyMetadata';
import { getI18nInstance, supportedLanguages, type SupportedLanguage } from '../i18n/initI18n';

const POLICY_ACCEPTANCE_STORAGE_KEY = 'phototalks:policy-acceptance';

function isBrowserEnvironment() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export type StoredPolicyAcceptance = PolicyAcceptancePayload;

export function getStoredPolicyAcceptance(): StoredPolicyAcceptance | null {
  if (!isBrowserEnvironment()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(POLICY_ACCEPTANCE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredPolicyAcceptance;
    return parsed?.version ? parsed : null;
  } catch {
    return null;
  }
}

export function recordPolicyAcceptance(payload: PolicyAcceptancePayload) {
  if (!isBrowserEnvironment()) {
    return;
  }
  try {
    window.localStorage.setItem(POLICY_ACCEPTANCE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Swallow storage exceptions (e.g., quota exceeded or privacy mode).
  }
}

export function needsPolicyReacceptance(): boolean {
  const stored = getStoredPolicyAcceptance();
  if (!stored) {
    return true;
  }
  return stored.version !== policyMetadata.version;
}

export function resolvePolicyLocale(): SupportedLanguage {
  const i18n = getI18nInstance();
  if (!i18n) {
    return 'en';
  }
  const rawLanguage = i18n.language?.split('-')[0] ?? 'en';
  return supportedLanguages.includes(rawLanguage as SupportedLanguage)
    ? (rawLanguage as SupportedLanguage)
    : 'en';
}
